import { dailySeed } from './sim/rng';
import {
  deserializeReplay,
  serializeReplay,
  type Replay,
  type SerializedReplay,
} from './sim/replay';
import type { ScoreBreakdown } from './sim/score';
import { SIM_VERSION } from './sim/version';
import type { Board } from './platform';

/**
 * 端末内の記録。
 *
 * Phase 1 ではここが唯一のランキングで、サーバーに繋がったら
 * `ILeaderboard` の実装が同じ形の記録を送る側に回る。
 * 形をここで確定させておくことで、繋ぎ替えのときにゲーム側を触らずに済む。
 */

const KEY = 'etw:records';
/** 1 つの表に残す件数。上位だけ残せば localStorage の容量に収まる。 */
const KEEP = 20;

export interface RunRecord {
  readonly stageId: string;
  readonly simVersion: number;
  readonly timeSeconds: number;
  readonly treasurePoints: number;
  readonly hatchlings: number;
  readonly bestCombo: number;
  readonly total: number;
  /** 記録した時刻（epoch ms）。同点の並び順に使う。 */
  readonly at: number;
}

interface StageRecords {
  runs: RunRecord[];
  /** ゴースト用。総合スコアが最も高かった走行のリプレイ。 */
  ghost?: SerializedReplay;
}

type Store = Record<string, StageRecords>;

/**
 * 表の鍵。デイリーチャレンジは日付ごとに別の表になる。
 * 同じシードで競うものと、好きなときに走るものを混ぜない。
 */
export function boardKey(stageId: string, dailyDateKey?: string): string {
  return dailyDateKey ? `${stageId}@${dailyDateKey}` : stageId;
}

/** UTC の日付キー。全プレイヤーで同じ区切りにする。 */
export function todayKey(date = new Date()): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

/** その日のデイリーチャレンジのシード。 */
export function dailyChallengeSeed(date = new Date()): number {
  return dailySeed(date);
}

/** 3 本すべてで上位に残るものだけを保持する。 */
function trim(entry: StageRecords): void {
  const keep = new Set<RunRecord>();
  for (const board of ['fastest', 'treasure', 'overall'] as const) {
    [...entry.runs].sort((a, b) => compare(board, a, b)).slice(0, KEEP).forEach((r) => keep.add(r));
  }
  entry.runs = [...keep];
}

function compare(board: Board, a: RunRecord, b: RunRecord): number {
  if (board === 'fastest') return a.timeSeconds - b.timeSeconds || a.at - b.at;
  if (board === 'treasure') return b.treasurePoints - a.treasurePoints || a.at - b.at;
  return b.total - a.total || a.at - b.at;
}

export class Records {
  private store: Store;

  constructor() {
    this.store = this.read();
  }

  /**
   * localStorage から読み直す。
   *
   * **同じ記録を 2 つのインスタンスが持つと必ずずれる。** 引き継ぎの取り込みは
   * 別のインスタンスが書くので、画面を持っている側はこれで拾い直す。
   */
  reload(): void {
    this.store = this.read();
  }

  private read(): Store {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Store) : {};
    } catch {
      return {};
    }
  }

  private write(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.store));
    } catch {
      // 容量超過や プライベートブラウズ。記録できなくても遊べる方を優先する。
    }
  }

  /**
   * 走行を記録する。
   * @returns 3 本それぞれの順位（1 始まり）。表に載らなければ null。
   */
  submit(
    key: string,
    stageId: string,
    score: ScoreBreakdown,
    replay: Replay | null,
  ): Record<Board, number | null> {
    const record: RunRecord = {
      stageId,
      simVersion: SIM_VERSION,
      timeSeconds: score.timeSeconds,
      treasurePoints: score.treasurePoints,
      hatchlings: score.hatchlings,
      bestCombo: score.bestCombo,
      total: score.total,
      at: Date.now(),
    };

    const entry = this.store[key] ?? { runs: [] };
    entry.runs.push(record);

    // 総合の最高記録だけをゴーストとして残す。
    // 全部残すと容量が持たないし、並走したいのは「自分の最高の走り」だけ。
    const bestTotal = entry.runs.reduce((a, r) => Math.max(a, r.total), 0);
    if (replay && record.total >= bestTotal) entry.ghost = serializeReplay(replay);

    trim(entry);
    this.store[key] = entry;
    this.write();

    const ranks = {} as Record<Board, number | null>;
    for (const board of ['fastest', 'treasure', 'overall'] as const) {
      const index = this.top(key, board, KEEP).findIndex((r) => r.at === record.at);
      ranks[board] = index >= 0 ? index + 1 : null;
    }
    return ranks;
  }

  top(key: string, board: Board, limit = 10): RunRecord[] {
    const entry = this.store[key];
    if (!entry) return [];
    return [...entry.runs].sort((a, b) => compare(board, a, b)).slice(0, limit);
  }

  best(key: string, board: Board): RunRecord | null {
    return this.top(key, board, 1)[0] ?? null;
  }

  runCount(key: string): number {
    return this.store[key]?.runs.length ?? 0;
  }

  /**
   * 並走させるゴースト。
   * **挙動バージョンが違うリプレイは返さない。** 再生しても当時の走りにならないので、
   * 並べると嘘のゴーストになる。
   */
  ghost(key: string): Replay | null {
    const serialized = this.store[key]?.ghost;
    if (!serialized) return null;
    const replay = deserializeReplay(serialized);
    return replay.simVersion === SIM_VERSION ? replay : null;
  }

  /** そのステージに一度でも帰還したか。ステージ選択の表示に使う。 */
  hasCleared(key: string): boolean {
    return this.runCount(key) > 0;
  }

  /**
   * 引き継ぎコードで取り戻した記録を取り込む。
   *
   * **上書きではなく併合。** この端末で走ったぶんを消さない。
   * サーバーは 1 つの表につき自己ベスト 1 件しか持たないので、
   * 戻ってくるのは表ごとの最高記録になる。
   *
   * @returns 実際に増えた件数。
   */
  importRemote(rows: RemoteRun[]): number {
    let added = 0;
    for (const row of rows) {
      // **挙動バージョンが違う記録は混ぜない。** 同じ表で競わせられない。
      if (row.simVersion !== SIM_VERSION) continue;

      const entry = this.store[row.boardKey] ?? { runs: [] };
      // 同じ走行を二度取り込まない（同じコードを何度入れても増えない）。
      const known = entry.runs.some(
        (r) =>
          r.total === row.total &&
          r.timeSeconds === row.timeSeconds &&
          r.treasurePoints === row.treasurePoints,
      );
      if (!known) {
        entry.runs.push({
          stageId: row.stageId,
          simVersion: row.simVersion,
          timeSeconds: row.timeSeconds,
          treasurePoints: row.treasurePoints,
          hatchlings: row.hatchlings,
          bestCombo: row.bestCombo,
          total: row.total,
          at: row.at,
        });
        added += 1;
      }

      // ゴーストは総合が上のものだけ差し替える。並走したいのは自分の最高の走り。
      const bestLocal = entry.runs.reduce((a, r) => Math.max(a, r.total), 0);
      if (row.ghost && row.total >= bestLocal) entry.ghost = row.ghost;

      trim(entry);
      this.store[row.boardKey] = entry;
    }
    if (added > 0) this.write();
    return added;
  }
}

/** サーバーから戻ってくる 1 件。`api/src/index.ts` の /player/restore と対。 */
export interface RemoteRun {
  readonly boardKey: string;
  readonly stageId: string;
  readonly simVersion: number;
  readonly timeSeconds: number;
  readonly treasurePoints: number;
  readonly hatchlings: number;
  readonly bestCombo: number;
  readonly total: number;
  readonly at: number;
  readonly ghost?: SerializedReplay | null;
}
